import type { ActionButtonsProps } from '../types/components'
import type { ConfirmationModalProps } from '../types/modals'
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { usePermission } from '@/hooks/usePermission'
import {
  translateApplication,

  updateApplicationStatus,
  approveApplication,
  expireApplication,
  resetApplication,
  unsuspendAccount,
} from '../services/applicationService'
import { LinkApplicantModal } from './modals/LinkApplicantModal'
import { SwapContactsModal } from './modals/SwapContactsModal'
import { OverrideStatusModal } from './modals/OverrideStatusModal'
import { DeniedStatusModal } from './modals/DeniedStatusModal'
import { AwaitingStatusModal } from './modals/AwaitingStatusModal'
import { ConfirmationModal } from './modals/ConfirmationModal'
import { EmailDropdown } from './EmailDropdown'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { isDeniedRiskRating } from '../utils/riskRatingAccessFee'
import { isScreeningInProcessProgressStatus, CANCEL_APPLICATION_NOTIFY_DIALOG_ROLES } from '../utils/applicationConstants'
import { formatEmail } from '../utils/applicationFormatters'
import styles from '../styles/actionButtons.module.css'


export function ActionButtons({
  applicationAllData,
  applicationData,
  applicantId,
  selectedTab,
  isCoresident,
  onRefresh,
  onEditToggle,
}: ActionButtonsProps) {
  const user = useAppSelector(selectUser)
  const { hasPolicy } = usePermission()
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showSwapModal, setShowSwapModal] = useState(false)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [showDeniedModal, setShowDeniedModal] = useState(false)
  const [deniedModalPurpose, setDeniedModalPurpose] = useState<'deny_applicant' | 'resend_denied_email'>(
    'deny_applicant',
  )
  const [showAwaitingModal, setShowAwaitingModal] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirmationData, setConfirmationData] = useState<Omit<ConfirmationModalProps, 'onClose'> | null>(null)
  const [activeAction, setActiveAction] = useState<string | null>(null)

  // Helper to check if any action is in progress
  const loading = activeAction !== null

  // Policy checks — server still enforces; UI mirrors Angular `authService.hasPolicy` gating.
  const canLinkApplicant = hasPolicy(['application_detail_link_applicant'])
  const canAction = hasPolicy(['application_action'])
  const canOverride = hasPolicy(['application_override'])
  const canApprove = hasPolicy(['application_approved'])
  const canDeny = hasPolicy(['application_denied'])
  const canContinue = hasPolicy(['application_continue'])
  const canEmailCreditError = hasPolicy(['application_email_credit_report_error'])
  const canEmailLibertyInfo = hasPolicy(['application_email_liberty_info'])

  // Button state checks
  const canEdit =
    selectedTab === 'aboutyourselfinfo' ||
    selectedTab === 'propertyinfo' ||
    selectedTab === 'employmentinfo'

  // Approved: unchanged except denied risk rating (plain `DENIED` or DAF `DENIED - 0`).
  const riskRatingDenied = isDeniedRiskRating(applicationAllData?.risk_rating)

  const canApproveButton =
    (applicationData?.applicant_status === 'PENDING' ||
      applicationData?.applicant_status === 'DENIED' ||
      applicationData?.applicant_status === 'CANCELLED') &&
    applicationAllData?.rent_amount &&
    !riskRatingDenied

  const canDenyButton =
    applicationData?.applicant_status === 'PENDING' ||
    (applicationData?.applicant_status === 'APPROVED' && !applicationAllData?.rent_added)

  const canContinueButton =
    (applicationData?.applicant_status === 'PENDING' ||
      applicationData?.applicant_status === 'AWAITING') &&
    !isScreeningInProcessProgressStatus(applicationData?.progress_status) &&
    applicationData?.application_fee

  // Angular `noemail` pipe: placeholder @noemail.com addresses count as no email.
  const hasEmail = Boolean(formatEmail(applicationData?.user_data?.email ?? ''))

  const canAwaitingButton =
    !(
      (applicationData?.applicant_status === 'APPROVED' && applicationAllData?.rent_added) ||
      applicationData?.applicant_status === 'CANCELLED'
    ) &&
    hasEmail &&
    applicationData?.is_approved_require !== '0'
  const isExpired = applicationData?.applicant_status === 'EXPIRED'
  const isCancelled = applicationData?.applicant_status === 'CANCELLED'
  const canExpire = !applicationAllData?.rent_added

  // Handle Translate
  const handleTranslate = async () => {
    if (loading) return
    setConfirmationData({
      title: 'Language',
      message: 'Change application detail to english',
      onConfirm: async () => {
        setActiveAction('translate')
        try {
          await translateApplication(applicantId)
          showSuccess('Translated Successfully')
          onRefresh()
        } catch (error: unknown) {
          showError((error as { message?: string })?.message ||'Failed to translate application')
        } finally {
          setActiveAction(null)
        }
      },
    })
    setShowConfirmation(true)
  }

  // Handle Edit - Toggle edit mode (no redirect, UI stays the same)
  const handleEdit = () => {
    if (loading) return
    // Angular editApplicant(): reactivate when application_status is CANCELLED (not applicant_status).
    if (applicationAllData?.application_status === 'CANCELLED') {
      handleReactiveCancel()
    } else {
      // Toggle edit mode
      trackApplicationEvent('application_edit_mode_toggled', 'application_review')
      if (onEditToggle) {
        onEditToggle()
      }
    }
  }

  // Handle Override
  const handleOverride = () => {
    if (loading) return
    setShowOverrideModal(true)
  }

  // Handle Approved
  const handleApproved = async () => {
    if (loading) return
    if (applicationData?.credit_report !== '1') {
      setConfirmationData({
        title: 'Status Update',
        message: 'Credit Report not created, Do you want to continue?',
        onConfirm: async () => {
          setTimeout(async () => {
            await performApproved()
          }, 100)
        },
      })
      setShowConfirmation(true)
    } else {
      await performApproved()
    }
  }

  const performApproved = async () => {
    // Check for co-applicants that don't require underwriting
    let coApplicantRequired = false
    if (applicationAllData?.co_resident_application) {
      Object.values(applicationAllData.co_resident_application as Record<string, { is_approved_require?: string; is_primary?: boolean }>).forEach((coApp) => {
        if (coApp.is_approved_require !== '1' && !coApp.is_primary) {
          coApplicantRequired = true
        }
      })
    }

    if (applicationData?.is_primary && coApplicantRequired) {
      setConfirmationData({
        title: 'Approved Applicant',
        message: `One or more co-applicants are marked as Not Requiring Underwriting. Do you want to<br>
        (1) Approve the Application now to allow them to buy the Certificate, or<br>
        (2) mark the Co-applicants as required first, then Approve this Applicant?<br>`,
        positiveTitle: 'Approve Now',
        negativeTitle: 'Mark Co-Apps Req. & Approve',
        onConfirm: async () => {
          setActiveAction('approve')
          try {
            await approveApplication(applicantId, { applicant_status: 'APPROVED' })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'approved',
            })
            showSuccess('Status Changed Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to approve application')
          } finally {
            setActiveAction(null)
          }
        },
        onCancel: async () => {
          setActiveAction('approve')
          try {
            await approveApplication(applicantId, {
              applicant_status: 'APPROVED',
              all_require: true,
            })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'approved',
            })
            showSuccess('Status Changed Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to approve application')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    } else {
      setConfirmationData({
        title: 'Status Update',
        message: 'Update Status to Approved',
        positiveTitle: 'Yes',
        negativeTitle: 'No',
        onConfirm: async () => {
          setActiveAction('approve')
          try {
            await approveApplication(applicantId, { applicant_status: 'APPROVED' })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'approved',
            })
            showSuccess('Status Changed Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to approve application')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    }
  }

  // Handle Denied — mirrors Angular handleActionsButtons → performButtonAction → updateStatusDenied.
  const handleDenied = () => {
    if (loading) return

    const openDeniedModal = () => {
      setDeniedModalPurpose('deny_applicant')
      setShowDeniedModal(true)
    }

    if (applicationData?.credit_report !== '1') {
      setConfirmationData({
        title: 'Status Update',
        message: 'Credit Report not created, Do you want to continue?',
        onConfirm: () => {
          setTimeout(() => openDeniedModal(), 0)
        },
      })
      setShowConfirmation(true)
    } else {
      openDeniedModal()
    }
  }

  // Continue — credit confirm if needed, then "Update Status to Continue" (Angular).
  const handleContinue = () => {
    if (loading) return

    const executeContinueUnderwriting = async () => {
      setActiveAction('continue')
      try {
        await updateApplicationStatus(applicantId, {
          applicant_status: 'UNDERWRITING_IN_PROGRESS',
        })
        trackApplicationEvent('underwriting_continued', 'application_review')
        showSuccess('Status Changed Successfully!')
        onRefresh()
      } catch (error: unknown) {
        showError((error as { message?: string })?.message ||'Failed to continue underwriting')
      } finally {
        setActiveAction(null)
      }
    }

    const openContinueStatusConfirm = () => {
      setConfirmationData({
        title: 'Status Update',
        message: 'Update Status to Continue',
        positiveTitle: 'Yes',
        negativeTitle: 'No',
        onConfirm: () => {
          void executeContinueUnderwriting()
        },
      })
      setShowConfirmation(true)
    }

    if (applicationData?.credit_report !== '1') {
      setConfirmationData({
        title: 'Status Update',
        message: 'Credit Report not created, Do you want to continue?',
        positiveTitle: 'Yes',
        negativeTitle: 'No',
        onConfirm: () => {
          setTimeout(() => openContinueStatusConfirm(), 0)
        },
      })
      setShowConfirmation(true)
    } else {
      openContinueStatusConfirm()
    }
  }

  // Handle Awaiting
  const handleAwaiting = () => {
    if (loading) return
    if (isCoresident) {
      setShowAwaitingModal(true)
    } else {
      setConfirmationData({
        title: 'Status Update',
        message: `Update Status to AWAITING`,
        onConfirm: async () => {
          setActiveAction('awaiting')
          try {
            await updateApplicationStatus(applicantId, {
              applicant_status: 'AWAITING',
              credit_report_process: '',
            })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'awaiting',
            })
            showSuccess('Status Changed Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to update status to AWAITING')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    }
  }

  // Handle Expire/UnExpire
  const handleExpire = async () => {
    if (loading) return
    if (isExpired) {
      // UnExpire
      setConfirmationData({
        title: 'Reset Expire Application',
        message: 'Are you sure you want to Un-Expire this Application?',
        onConfirm: async () => {
          setActiveAction('expire')
          try {
            const applicationId = applicationAllData.application_id;
            if (applicationId == null) {
              showError('Application id is missing');
              return;
            }
            await resetApplication(applicationId, {
              applicant_status: 'EXPIRED',
            })
            showSuccess('Application Status Reset Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to unexpire application')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    } else {
      // Expire
      setConfirmationData({
        title: 'Confirm Action?',
        message: 'Should the applicant be blocked for 30 days based on this action?',
        positiveTitle: 'Yes',
        negativeTitle: 'No',
        onConfirm: async () => {
          setActiveAction('expire')
          try {
            await expireApplication(applicantId, { account_suspended: '1' })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'expired',
            })
            showSuccess('Status Changed Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to expire application')
          } finally {
            setActiveAction(null)
          }
        },
        onCancel: async () => {
          setActiveAction('expire')
          try {
            await expireApplication(applicantId, { account_suspended: '' })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'expired',
            })
            showSuccess('Status Changed Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to expire application')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    }
  }

  // Handle Cancel/Uncancel
  const handleCancel = async () => {
    if (loading) return
    if (isCancelled) {
      // Uncancel
      setConfirmationData({
        title: 'Application Cancelled',
        message: 'This application is Cancelled. Do you want to reactivate it?',
        positiveTitle: 'Continue',
        negativeTitle: 'Cancel',
        onConfirm: async () => {
          setActiveAction('cancel')
          try {
            const applicationId = applicationAllData.application_id;
            if (applicationId == null) {
              showError('Application id is missing');
              return;
            }
            await resetApplication(applicationId, {
              applicant_status: 'CANCELLED',
            })
            showSuccess('Application Status Reset Successfully!')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to uncancel application')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    } else {
      const roleName = user?.current_role?.role_name
      const canChooseCancelNotification =
        roleName != null && CANCEL_APPLICATION_NOTIFY_DIALOG_ROLES.includes(roleName)

      // Angular cancelApplicationPopup: non-allowed roles cancel immediately (no dialog, no mail).
      if (!canChooseCancelNotification) {
        setActiveAction('cancel')
        try {
          await updateApplicationStatus(applicantId, {
            applicant_status: 'CANCELLED',
          })
          trackApplicationEvent('application_status_updated', 'application_review', {
            application_status: 'cancelled',
          })
          showSuccess('Application has been canceled.')
          onRefresh()
        } catch (error: unknown) {
          showError((error as { message?: string })?.message ||'Failed to cancel application')
        } finally {
          setActiveAction(null)
        }
        return
      }

      // Cancel dialog copy (Angular cancelApplicationPopup).
      setConfirmationData({
        title: 'Application Cancellation',
        message: `<p style="margin:0 0 12px;">You have chosen to cancel this application. Please choose the appropriate next step:</p>
<ul style="margin:0 0 12px; padding-left:20px; list-style:disc;">
  <li style="margin-bottom:8px;"><b>Cancel and Notify:</b> A cancellation email will be sent to the Property.</li>
  <li><b>Cancel without Notification:</b> A refund has been issued or a chargeback has been processed, OR a refund is not necessary. Cancel the Application immediately</li>
</ul>
<p style="margin:0;">Please close this window to return to the Application without taking action.</p>`,
        positiveTitle: 'Cancel and Notify',
        negativeTitle: 'Cancel without Notification',
        onConfirm: async () => {
          setActiveAction('cancel')
          try {
            await updateApplicationStatus(applicantId, {
              applicant_status: 'CANCELLED',
              send_cancelled_mail: true,
            })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'cancelled',
            })
            showSuccess('Application has been canceled.')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to cancel application')
          } finally {
            setActiveAction(null)
          }
        },
        onCancel: async () => {
          setActiveAction('cancel')
          try {
            await updateApplicationStatus(applicantId, {
              applicant_status: 'CANCELLED',
              send_cancelled_mail: false,
            })
            trackApplicationEvent('application_status_updated', 'application_review', {
              application_status: 'cancelled',
            })
            showSuccess('Application has been canceled.')
            onRefresh()
          } catch (error: unknown) {
            showError((error as { message?: string })?.message ||'Failed to cancel application')
          } finally {
            setActiveAction(null)
          }
        },
      })
      setShowConfirmation(true)
    }
  }

  // Handle Reactive Cancel
  const handleReactiveCancel = () => {
    setConfirmationData({
      title: 'Application Cancelled',
      message: 'This application is Cancelled. Do you want to reactivate it?',
      positiveTitle: 'Continue',
      negativeTitle: 'Cancel',
      onConfirm: async () => {
        setActiveAction('reactive_cancel')
        try {
          const applicationId = applicationAllData.application_id;
          if (applicationId == null) {
            showError('Application id is missing');
            return;
          }
          await resetApplication(applicationId, {
            applicant_status: 'CANCELLED',
          })
          showSuccess('Application Status Reset Successfully!')
          onRefresh()
        } catch (error: unknown) {
          showError((error as { message?: string })?.message ||'Failed to reactivate application')
        } finally {
          setActiveAction(null)
        }
      },
    })
    setShowConfirmation(true)
  }

  const requestUnderwritingChange = (isApprovedRequire: '1' | '0') => {
    if (loading) return
    if (applicationData?.is_approved_require === isApprovedRequire) return

    setConfirmationData({
      title: 'Confirm',
      message:
        isApprovedRequire === '1'
          ? 'Change Underwriting Required to Yes'
          : 'Change Underwriting Required to No',
      onConfirm: async () => {
        setActiveAction(isApprovedRequire === '1' ? 'underwriting_yes' : 'underwriting_no')
        try {
          await updateApplicationStatus(applicantId, {
            is_approved_require: isApprovedRequire,
          })
          trackApplicationEvent('underwriting_requirement_updated', 'application_review', {
            action_result: isApprovedRequire === '1' ? 'required' : 'not_required',
          })
          showSuccess('Underwriting Required Change Successfully')
          onRefresh()
        } catch (error: unknown) {
          showError((error as { message?: string })?.message ||'Failed to update underwriting requirement')
        } finally {
          setActiveAction(null)
        }
      },
    })
    setShowConfirmation(true)
  }

  /** Angular: only the primary action column is hidden; Applicant Links / Swap + UW Required stay visible. */
  const hidePrimaryActionsForCsCheckout =
    user?.current_role?.role_name === 'ROLE_CUSTOMER_SERVICE' &&
    Boolean(applicationData?.underwriting_by)

  /** Angular `application-details.component.html`: Cancel/Uncancel `d-none` for CS when status is CANCELLED. */
  const hideCancelForCsWhenCancelled =
    user?.current_role?.role_name === 'ROLE_CUSTOMER_SERVICE' && isCancelled

  return (
    <div className={styles.actionButtonsContainer} data-testid="application-detail-action-buttons">
      {/* Left Column - Underwriting Required & Applicant Links */}
      <div className={styles.leftColumn} data-testid="application-detail-action-left-column">
        {/* Underwriting Required toggle — only for non-primary applicants */}
        {!applicationData?.is_primary && (
          <fieldset
            className={`${styles.underwritingSection} ${styles.underwritingFieldset}`}
            data-testid="application-detail-action-underwriting-required-fieldset"
          >
            <legend className={styles.underwritingLabel} data-testid="application-detail-action-underwriting-required-legend">
              Underwriting Required?
            </legend>
            <div className={styles.underwritingToggleGroup} data-testid="application-detail-action-underwriting-required-toggle">
              <button
                type="button"
                className={`${styles.underwritingToggleBtn} ${
                  applicationData?.is_approved_require === '1' ? styles.underwritingToggleBtnActive : ''
                }`}
                data-testid="application-detail-action-underwriting-required-yes-button"
                onClick={() => requestUnderwritingChange('1')}
                disabled={loading}
                aria-pressed={applicationData?.is_approved_require === '1'}
              >
                Yes
              </button>
              <button
                type="button"
                className={`${styles.underwritingToggleBtn} ${
                  applicationData?.is_approved_require === '0' ? styles.underwritingToggleBtnActive : ''
                }`}
                data-testid="application-detail-action-underwriting-required-no-button"
                onClick={() => requestUnderwritingChange('0')}
                disabled={loading}
                aria-pressed={applicationData?.is_approved_require === '0'}
              >
                No
              </button>
            </div>
          </fieldset>
        )}

        {/* Applicant Links Buttons */}
        {canLinkApplicant && (
          <div className={styles.linkButtons} data-testid="application-detail-action-link-buttons">
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              data-testid="application-detail-action-applicant-links-button"
              onClick={() => !loading && setShowLinkModal(true)}
              disabled={loading && activeAction === 'link'}
            >
              Applicant Links
            </button>
            {isCoresident && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                data-testid="application-detail-action-swap-contacts-button"
                onClick={() => !loading && setShowSwapModal(true)}
                disabled={loading}
              >
                Swap Contacts
              </button>
            )}
          </div>
        )}
      </div>

      {/* Right column — Angular hides this block when CS has checked out underwriting (`underwriting_by`). */}
      {canAction && !hidePrimaryActionsForCsCheckout && (
        <div className={styles.rightColumn} data-testid="application-detail-action-right-column">
          <div className={styles.actionButtons} data-testid="application-detail-action-primary-buttons">
            <button
              type="button"
              className={styles.btn}
              data-testid="application-detail-action-translate-button"
              onClick={handleTranslate}
              disabled={activeAction === 'translate'}
            >
              <span className={activeAction === 'translate' ? 'invisible' : ''}>Translate</span>
              {activeAction === 'translate' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="animate-spin h-4 w-4" />
                </div>
              )}
            </button>

            <button
              type="button"
              className={styles.btn}
              data-testid="application-detail-action-edit-button"
              onClick={handleEdit}
              disabled={!canEdit}
            >
              Edit
            </button>

            {canOverride && (
              <button
                type="button"
                className={styles.btn}
                data-testid="application-detail-action-override-button"
                onClick={handleOverride}
                disabled={false}
              >
                Override
              </button>
            )}

            {canApprove && (
              <button
                type="button"
                className={styles.btn}
                data-testid="application-detail-action-approved-button"
                onClick={handleApproved}
                disabled={!canApproveButton || activeAction === 'approve'}
              >
                <span className={activeAction === 'approve' ? 'invisible' : ''}>Approved</span>
                {activeAction === 'approve' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="animate-spin h-4 w-4" />
                  </div>
                )}
              </button>
            )}

            {canDeny && (
              <button
                type="button"
                className={styles.btn}
                data-testid="application-detail-action-denied-button"
                onClick={handleDenied}
                disabled={!canDenyButton || activeAction === 'deny'}
              >
                <span className={activeAction === 'deny' ? 'invisible' : ''}>Denied</span>
                {activeAction === 'deny' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="animate-spin h-4 w-4" />
                  </div>
                )}
              </button>
            )}

            {/* Un-Suspend button - conditional on policy and account_suspended */}
            {hasPolicy(['application_unsuspend']) &&
              applicationData?.account_suspended === '1' && (
                <button
                  type="button"
                  className={styles.btn}
                  data-testid="application-detail-action-unsuspend-button"
                  onClick={async () => {
                    if (loading) return
                    setConfirmationData({
                      title: 'Confirm',
                      message: 'Un-Suspend Applicant Account',
                      onConfirm: async () => {
                        setActiveAction('unsuspend')
                        try {
                          await unsuspendAccount(applicantId)
                          showSuccess('Account Suspended Remove Successfully')
                          onRefresh()
                        } catch (error: unknown) {
                          showError((error as { message?: string })?.message ||'Failed to unsuspend account')
                        } finally {
                          setActiveAction(null)
                        }
                      },
                    })
                    setShowConfirmation(true)
                  }}
                  disabled={activeAction === 'unsuspend'}
                >
                  <span className={activeAction === 'unsuspend' ? 'invisible' : ''}>Un-Suspend</span>
                  {activeAction === 'unsuspend' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="animate-spin h-4 w-4" />
                    </div>
                  )}
                </button>
              )}

            {canContinue && (
              <button
                type="button"
                className={styles.btn}
                data-testid="application-detail-action-continue-button"
                onClick={handleContinue}
                disabled={!canContinueButton || activeAction === 'continue'}
              >
                <span className={activeAction === 'continue' ? 'invisible' : ''}>Continue</span>
                {activeAction === 'continue' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="animate-spin h-4 w-4" />
                  </div>
                )}
              </button>
            )}

            <button
              type="button"
              className={styles.btn}
              data-testid="application-detail-action-awaiting-button"
              onClick={handleAwaiting}
              disabled={!canAwaitingButton || activeAction === 'awaiting'}
            >
              <span className={activeAction === 'awaiting' ? 'invisible' : ''}>Awaiting</span>
              {activeAction === 'awaiting' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="animate-spin h-4 w-4" />
                </div>
              )}
            </button>

            <EmailDropdown
              applicantId={applicantId}
              applicationAllData={applicationAllData}
              applicationData={applicationData}
              canEmailCreditError={canEmailCreditError}
              canEmailLibertyInfo={canEmailLibertyInfo}
              disabled={!hasEmail}
              onEmailSent={onRefresh}
              onResendDeniedEmail={() => {
                if (loading) return
                setDeniedModalPurpose('resend_denied_email')
                setShowDeniedModal(true)
              }}
            />

            {canExpire && (
              <button
                type="button"
                className={styles.btn}
                data-testid="application-detail-action-expire-button"
                onClick={handleExpire}
                disabled={activeAction === 'expire'}
              >
                <span className={activeAction === 'expire' ? 'invisible' : ''}>
                  {isExpired ? 'UnExpire' : 'Expire'}
                </span>
                {activeAction === 'expire' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="animate-spin h-4 w-4" />
                  </div>
                )}
              </button>
            )}

            {!hideCancelForCsWhenCancelled && (
              <button
                type="button"
                className={styles.btn}
                data-testid="application-detail-action-cancel-button"
                onClick={handleCancel}
                disabled={activeAction === 'cancel' || activeAction === 'reactive_cancel'}
              >
                <span
                  className={
                    activeAction === 'cancel' || activeAction === 'reactive_cancel' ? 'invisible' : ''
                  }
                >
                  {isCancelled ? 'Uncancel' : 'Cancel'}
                </span>
                {(activeAction === 'cancel' || activeAction === 'reactive_cancel') && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="animate-spin h-4 w-4" />
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showLinkModal && (
        <LinkApplicantModal
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          isCoresident={isCoresident}
          onClose={() => setShowLinkModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {showSwapModal && (
        <SwapContactsModal
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          onClose={() => setShowSwapModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {showOverrideModal && (
        <OverrideStatusModal
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          onClose={() => setShowOverrideModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {showDeniedModal && (
        <DeniedStatusModal
          key={deniedModalPurpose}
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          purpose={deniedModalPurpose}
          onClose={() => setShowDeniedModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {showAwaitingModal && (
        <AwaitingStatusModal
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          isCoresident={isCoresident}
          onClose={() => setShowAwaitingModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {showConfirmation && confirmationData && (
        <ConfirmationModal
          title={confirmationData.title}
          message={confirmationData.message}
          positiveTitle={confirmationData.positiveTitle || 'Yes'}
          negativeTitle={confirmationData.negativeTitle || 'No'}
          presentation={confirmationData.presentation ?? 'default'}
          onConfirm={() => {
            if (confirmationData.onConfirm) {
              confirmationData.onConfirm()
            }
            setShowConfirmation(false)
            setConfirmationData(null)
          }}
          onCancel={() => {
            if (confirmationData.onCancel) {
              confirmationData.onCancel()
            }
            setShowConfirmation(false)
            setConfirmationData(null)
          }}
          onClose={() => {
            setShowConfirmation(false)
            setConfirmationData(null)
          }}
        />
      )}
    </div>
  )
}

